import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { listOffers, OFFER_STATUS_LABELS, OFFER_STATUS_COLORS, extractApiError } from '../../lib/subcontractOfferApi';
import { toast } from 'sonner';

/**
 * Lista Offerte di Subappalto (revisioni correnti) per un progetto.
 *
 * Props:
 * - projectId
 * - refreshKey (bump per riload)
 * - onOpenOffer: (offer) => void
 */
export const SubcontractOffersList = ({ projectId, refreshKey, onOpenOffer }) => {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listOffers(projectId, { current_revision_only: true })
      .then(res => { if (!cancelled) { setOffers(res.items || []); setError(null); } })
      .catch(err => {
        const msg = extractApiError(err, 'Errore caricamento preventivi');
        if (!cancelled) { setError(msg); toast.error(msg); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, refreshKey]);

  if (loading) return <div className="p-6 text-sm text-slate-500" data-testid="offers-list-loading">Caricamento preventivi...</div>;
  if (error) return <div className="p-6 text-sm text-rose-600" data-testid="offers-list-error">{error}</div>;

  return (
    <div data-testid="offers-list">
      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
        Offerte di Subappalto
      </h4>
      {offers.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center border border-dashed border-slate-200 rounded-md" data-testid="offers-list-empty">
          Nessuna offerta di subappalto ancora creato per questo progetto.
        </div>
      ) : (
        <div className="space-y-2">
          {offers.map(o => (
            <OfferRow key={o.id} offer={o} onOpen={onOpenOffer} />
          ))}
        </div>
      )}
    </div>
  );
};

const OfferRow = ({ offer, onOpen }) => {
  const items = offer.items || [];
  const total = offer.agreed_total_amount;
  return (
    <Card
      className="hover:shadow-md transition cursor-pointer"
      onClick={() => onOpen && onOpen(offer)}
      data-testid={`offer-row-${offer.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div>
              <div className="font-semibold" data-testid={`offer-row-number-${offer.id}`}>{offer.offer_number}</div>
              <div className="text-xs text-slate-500">
                Rev. {offer.offer_revision_number} · {(offer.supplier_snapshot?.ragione_sociale) || '—'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-slate-600">
              <div className="text-xs text-slate-500">Lavorazioni</div>
              <div>{items.length}</div>
            </div>
            <div className="text-slate-600">
              <div className="text-xs text-slate-500">Importo</div>
              <div data-testid={`offer-row-total-${offer.id}`}>
                {total ? `€ ${Number(total).toFixed(2)}` : <span className="text-slate-400">— (economia da definire)</span>}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Validità</div>
              <div>{offer.valid_until || <span className="text-slate-400">—</span>}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Stato</div>
              <Badge className={OFFER_STATUS_COLORS[offer.status] || ''} data-testid={`offer-row-status-${offer.id}`}>
                {OFFER_STATUS_LABELS[offer.status] || offer.status}
              </Badge>
            </div>
            {offer.converted_to_subcontract_id && (
              <div className="text-xs text-teal-700">↳ Contratto generato</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubcontractOffersList;
